import React from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/EditOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useTranslation } from 'react-i18next';
import { CategoryCardAccent } from '../category-card/types';
import styles from './ProfilesList.module.scss';

export type ProfileRowDetail = {
  /** Element id (kept for click tracking). */
  id: string;
  icon: React.ReactNode;
  /** Shown in a tooltip, as the icon alone does not say what the value is. */
  label: string;
  value?: string;
};

export type ProfileRowProps = {
  /** Prefix of the element ids, e.g. `customerRow`. */
  idPrefix: string;
  profileId: number;
  title: string;
  icon: React.ReactNode;
  accent: CategoryCardAccent;
  details: ProfileRowDetail[];
  onOpen: () => void;
  onDocuments: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

/**
 * Customer / supplier row, laid out as a Document Vault row: an icon tile, the name with the
 * contact details underneath, and the action buttons.
 */
export const ProfileRow = ({
  idPrefix,
  profileId,
  title,
  icon,
  accent,
  details,
  onOpen,
  onDocuments,
  onEdit,
  onDelete,
}: ProfileRowProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const filledDetails = details.filter((detail) => detail.value && detail.value.trim());

  return (
    <TableRow className={styles.row}>
      <TableCell
        id={`${idPrefix}NameCell_${profileId}`}
        className={`${styles.cell} ${styles.name_cell}`}
        onClick={onOpen}
      >
        <Box className={styles.name_container}>
          <Box
            className={`${styles.icon_tile} ${styles[`accent_${accent}`] ?? ''}`}
            aria-hidden='true'
          >
            {icon}
          </Box>
          <Box className={styles.text_container}>
            <Typography component='span' className={styles.title}>
              {title || '-'}
            </Typography>
            {filledDetails.length > 0 && (
              <Typography component='span' className={styles.subtitle}>
                {filledDetails.map((detail) => (
                  <Tooltip key={detail.id} title={detail.label} arrow placement='top'>
                    <span id={`${detail.id}_${profileId}`} className={styles.detail}>
                      <span className={styles.detail_icon} aria-hidden='true'>
                        {detail.icon}
                      </span>
                      <span className={styles.detail_text}>{detail.value}</span>
                    </span>
                  </Tooltip>
                ))}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell className={`${styles.cell} ${styles.actions_cell}`}>
        <Box className={styles.actions_container}>
          <Tooltip title={t('buttons.documents')} placement='top' arrow>
            <IconButton
              id={`${idPrefix}DocumentsIconBtn_${profileId}`}
              aria-label={t('buttons.documents')}
              size='small'
              className={styles.action_button}
              onClick={onDocuments}
            >
              <UploadFileOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('buttons.edit')} placement='top' arrow>
            <IconButton
              id={`${idPrefix}EditIconBtn_${profileId}`}
              aria-label={t('buttons.edit')}
              size='small'
              className={styles.action_button}
              onClick={onEdit}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <IconButton
            id={`${idPrefix}OpenMenuIconBtn_${profileId}`}
            aria-label={t('labels.menuButton')}
            size='small'
            className={styles.action_button}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <MoreVertIcon
              id={`${idPrefix}OpenMenuIconBtnMoreVertIcon_${profileId}`}
              fontSize='small'
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              id={`${idPrefix}DeleteMenuItem_${profileId}`}
              onClick={() => {
                setAnchorEl(null);
                onDelete();
              }}
            >
              {t('buttons.delete')}
            </MenuItem>
          </Menu>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default ProfileRow;
